import React, { useState } from 'react';
import { uploadDocument } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Progress } from '../ui/progress';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { DOCUMENT_TYPES } from './DossierHelpers';

const MAX_UPLOAD_SIZE_MB = 25;

const formatSize = (bytes) => {
    if (!bytes) return '';
    const mb = bytes / (1024 * 1024);
    if (mb >= 1) return `${mb.toFixed(1)} Mo`;
    const kb = bytes / 1024;
    return `${kb.toFixed(0)} Ko`;
};

export const UploadDocumentDialog = ({ open, onOpenChange, dossierId, onUploaded }) => {
    const [type, setType] = useState('devis');
    const [file, setFile] = useState(null);
    const [comment, setComment] = useState('');
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);

    const resetState = () => {
        setFile(null);
        setComment('');
        setProgress(0);
        setUploading(false);
    };

    const handleFileChange = (e) => {
        const f = e.target.files?.[0] || null;
        if (f && f.size > MAX_UPLOAD_SIZE_MB * 1024 * 1024) {
            toast.error(`Fichier trop volumineux (${formatSize(f.size)}). Limite : ${MAX_UPLOAD_SIZE_MB} Mo.`);
            e.target.value = '';
            setFile(null);
            return;
        }
        setFile(f);
    };

    const handleUpload = async () => {
        if (!file) return;
        setUploading(true);
        setProgress(0);
        try {
            await uploadDocument(dossierId, type, file, comment, (pct) => setProgress(pct));
            toast.success('Document uploadé');
            resetState();
            onOpenChange(false);
            onUploaded && onUploaded();
        } catch (err) {
            const serverDetail = err?.response?.data?.detail;
            if (err?.code === 'ECONNABORTED') {
                toast.error("L'upload a expiré. Vérifiez votre connexion et réessayez.");
            } else if (serverDetail) {
                toast.error(serverDetail);
            } else {
                toast.error("Erreur lors de l'upload");
            }
            setProgress(0);
        } finally {
            setUploading(false);
        }
    };

    const handleOpenChange = (openState) => {
        if (!openState && !uploading) {
            resetState();
        }
        onOpenChange(openState);
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Ajouter un document</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label className="field-label">Type de document</Label>
                        <Select value={type} onValueChange={setType} disabled={uploading}>
                            <SelectTrigger className="h-10 rounded-sm mt-1"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {DOCUMENT_TYPES.map((t) => (
                                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="field-label">Fichier (max {MAX_UPLOAD_SIZE_MB} Mo)</Label>
                        <Input
                            type="file"
                            onChange={handleFileChange}
                            className="h-10 rounded-sm mt-1"
                            accept=".jpg,.jpeg,.png,.webp,.pdf,.doc,.docx"
                            disabled={uploading}
                            data-testid="file-input"
                        />
                        {file && (
                            <div className="text-xs text-zinc-500 mt-1" data-testid="file-size-info">
                                {file.name} — {formatSize(file.size)}
                            </div>
                        )}
                    </div>
                    <div>
                        <Label className="field-label">Commentaire (optionnel)</Label>
                        <Textarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            rows={2}
                            className="rounded-sm mt-1"
                            placeholder="Description du document..."
                            disabled={uploading}
                        />
                    </div>
                    {uploading && (
                        <div className="space-y-2" data-testid="upload-progress">
                            <Progress value={progress} className="h-2 rounded-sm" />
                            <div className="text-xs text-zinc-500 flex justify-between">
                                <span>Upload en cours…</span>
                                <span>{progress}%</span>
                            </div>
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => handleOpenChange(false)}
                        disabled={uploading}
                    >
                        Annuler
                    </Button>
                    <Button
                        onClick={handleUpload}
                        disabled={!file || uploading}
                        className="bg-[#E85A24] hover:bg-[#CC4D1C] text-white"
                        data-testid="upload-submit-btn"
                    >
                        {uploading ? `Envoi… ${progress}%` : 'Ajouter'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
